import { useState } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Send,
  MessageCircle,
  Calendar,
  Car,
  Bus,
  Navigation as NavigationIcon,
  FileText,
  Download,
  User,
  CheckCircle
} from 'lucide-react';

const Contatti = () => {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefono: '',
    servizio: '',
    messaggio: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    toast.success("Messaggio inviato con successo! Ti ricontatteremo presto.");
    setFormData({ nome: '', email: '', telefono: '', servizio: '', messaggio: '' });
  };

  const infoContatti = [
    {
      icon: Phone,
      titolo: "Telefono",
      valore: "+39 347 5391433",
      descrizione: "Chiamaci per prenotazioni",
      link: "tel:+393475391433"
    },
    {
      icon: Mail,
      titolo: "Email",
      valore: "info@riabilitazionemotoriapapa.it",
      descrizione: "Scrivici per informazioni",
      link: "mailto:info@riabilitazionemotoriapapa.it"
    },
    {
      icon: MapPin,
      titolo: "Indirizzo",
      valore: "Via Nazionale Terme, 191",
      descrizione: "98050 Terme Vigliatore (ME)",
      link: "https://maps.google.com/?q=Via+Nazionale+Terme+191+Terme+Vigliatore+Messina"
    },
    {
      icon: Clock,
      titolo: "Orari",
      valore: "Lun-Ven: 8:00-19:00",
      descrizione: "Sabato su appuntamento",
      link: null
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5">
        <div className="container-custom">
          <div className="text-center space-y-4 mb-12">
            <Badge className="bg-primary/10 text-primary border-primary/20">
              Contatti
            </Badge>
            <h1 className="text-4xl lg:text-5xl font-bold">Contattaci</h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Siamo qui per aiutarti. Contattaci per prenotare una visita o per 
              richiedere informazioni o per qualsiasi domanda sui nostri servizi
            </p>
          </div>
        </div>
      </section>

      {/* Sezione Personale */}
      <section className="section-padding bg-muted/30">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <Card className="medical-card">
              <CardContent className="p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                  <div className="text-center md:text-left space-y-4">
                    <Badge className="bg-primary/10 text-primary border-primary/20">
                      Il Tuo Fisioterapista
                    </Badge>
                    <h2 className="text-3xl font-bold">Dott. Peppino Salvatore Papa</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      Sono qui per accompagnarti nel tuo percorso di riabilitazione con 
                      professionalità, esperienza e attenzione umana. Ogni paziente è unico 
                      e merita un trattamento personalizzato.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="secondary">25+ Anni Esperienza</Badge>
                      <Badge variant="secondary">TNPEE Specializzato</Badge>
                      <Badge variant="secondary">Tecnico Chinesiologo</Badge>
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <div className="relative">
                      <div className="w-64 h-64 rounded-2xl overflow-hidden shadow-2xl">
                        <img 
                          src="/images/IMG_3343.jpg" 
                          alt="Dott. Peppino Salvatore Papa - Fisioterapista" 
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="absolute -bottom-4 -right-4 bg-white p-3 rounded-full shadow-lg">
                        <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                          <CheckCircle className="h-5 w-5 text-white" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Informazioni di Contatto */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {infoContatti.map((info, index) => (
              <Card key={index} className="medical-card group hover:scale-105 smooth-transition">
                <CardHeader className="text-center">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 smooth-transition">
                    <info.icon className="h-8 w-8 text-primary" />
                  </div>
                  <CardTitle className="text-lg">{info.titolo}</CardTitle>
                </CardHeader>
                <CardContent className="text-center space-y-2">
                  {info.link ? (
                    <a 
                      href={info.link} 
                      className="block text-lg font-semibold text-primary hover:text-primary/80 smooth-transition"
                    >
                      {info.valore}
                    </a>
                  ) : (
                    <p className="text-lg font-semibold">{info.valore}</p>
                  )}
                  <p className="text-sm text-muted-foreground">{info.descrizione}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Mappa */}
          <div className="mb-16">
            <Card className="medical-card overflow-hidden">
              <CardContent className="p-0">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3024.123456789!2d15.123456!3d38.123456!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sVia%20Nazionale%20Terme%20191%2C%2098050%20Terme%20Vigliatore%20ME!5e0!3m2!1sit!2sit!4v1234567890"
                  width="100%"
                  height="400"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Centro di Riabilitazione Papa - Mappa"
                ></iframe>
              </CardContent>
            </Card>
          </div>

          {/* Form di Contatto */}
          <div className="max-w-2xl mx-auto">
            <Card className="medical-card">
              <CardHeader className="text-center">
                <CardTitle className="text-2xl">Invia un Messaggio</CardTitle>
                <CardDescription>
                  Compila il form per richiedere informazioni o prenotare una visita
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="nome">Nome Completo</Label>
                      <Input
                        id="nome"
                        value={formData.nome}
                        onChange={(e) => setFormData({...formData, nome: e.target.value})}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="telefono">Telefono</Label>
                      <Input
                        id="telefono"
                        type="tel"
                        value={formData.telefono}
                        onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="servizio">Servizio di Interesse</Label>
                    <Select value={formData.servizio} onValueChange={(value) => setFormData({...formData, servizio: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleziona un servizio" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fisioterapia">Fisioterapia</SelectItem>
                        <SelectItem value="riabilitazione">Riabilitazione Motoria</SelectItem>
                        <SelectItem value="posturale">Ginnastica Posturale</SelectItem>
                        <SelectItem value="tnpee">Terapia Neuropsicomotoria</SelectItem>
                        <SelectItem value="neonatale">Disturbi Motori Neonatali</SelectItem>
                        <SelectItem value="altro">Altro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="messaggio">Messaggio</Label>
                    <Textarea
                      id="messaggio"
                      rows={4}
                      value={formData.messaggio}
                      onChange={(e) => setFormData({...formData, messaggio: e.target.value})}
                      placeholder="Descrivi brevemente la tua richiesta..."
                      required
                    />
                  </div>

                  <Button type="submit" size="lg" className="w-full medical-gradient text-white">
                    <Send className="mr-2 h-4 w-4" />
                    Invia Messaggio
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contatti;